import React from 'react';
import { TailwindButton } from '../Tailwind';
import { cn } from '../../lib/utils';

export interface FMEPageHeaderProps {
  title: string;
  breadcrumb?: string | string[];
  onBreadcrumbClick?: (index: number) => void;
  description?: string;
  actions?: React.ReactNode;
  titleExtra?: React.ReactNode;
  className?: string;
}

export default function FMEPageHeader({
  title,
  breadcrumb,
  onBreadcrumbClick,
  description,
  actions,
  titleExtra,
  className,
}: FMEPageHeaderProps) {
  // Gérer breadcrumb comme array ou string
  const breadcrumbItems = Array.isArray(breadcrumb) ? breadcrumb : breadcrumb ? [breadcrumb] : [];

  return (
    <div className={cn(
      'px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10',
      className
    )}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left side: Breadcrumb + Title + Description */}
        <div className="flex-1">
          {/* Breadcrumb */}
          {breadcrumbItems.length > 0 && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-primary uppercase bg-primary/10 px-2 py-0.5 rounded tracking-widest">
                {breadcrumbItems[0]}
              </span>
              {breadcrumbItems.slice(1).map((item, index) => (
                <React.Fragment key={index}>
                  <span className="text-slate-300 text-xs">/</span>
                  {onBreadcrumbClick ? (
                    <button
                      onClick={() => onBreadcrumbClick(index + 1)}
                      className="text-slate-400 text-xs font-medium hover:text-primary hover:underline transition-colors"
                    >
                      {item}
                    </button>
                  ) : (
                    <span className="text-slate-400 text-xs font-medium">{item}</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Title */}
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
              {title}
            </h2>
            {titleExtra}
          </div>

          {/* Description (optional) */}
          {description && (
            <p className="text-sm text-slate-500 mt-2">
              {description}
            </p>
          )}
        </div>

        {/* Right side: Actions */}
        {actions && (
          <div className="flex items-center gap-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Exemple d'utilisation :
 *
 * <FMEPageHeader
 *   title="Financial Model Editor"
 *   breadcrumb={["Model Name", "Scenario Name"]}
 *   description="Configure your financial model structure and inputs"
 *   actions={
 *     <>
 *       <TailwindButton variant="secondary" icon="save">
 *         Save Draft
 *       </TailwindButton>
 *       <TailwindButton variant="primary" icon="rocket_launch">
 *         Run Model
 *       </TailwindButton>
 *     </>
 *   }
 * />
 */
